import { defineStore } from 'pinia'
import {
  type ProductFilter,
  type PriceRangeFilter,
} from '~/types/api/data/product/filter/filter.types'
import type { SelectedFilterParams } from '~/types/stores/product-listing/filters-store.types'

export const useProductListingFiltersStore = defineStore(
  'productListingFiltersStore',
  () => {
    /**
     * - - - - - States - - - - -
     */
    const selectedFilterParams = ref<SelectedFilterParams>({
      brands: [],
      priceRange: {
        'min-price': '',
        'max-price': '',
      },
      types: {},
      others: [],
    })

    const selectedCategory = ref<string>('')
    const toCloseAllMwebModals = ref(false)

    /**
     * - - - - - Functions - - - - -
     */
    function setSelectedCategory(category: string) {
      selectedCategory.value = category
    }

    function setToCloseAllMwebModals(value: boolean) {
      toCloseAllMwebModals.value = value
    }

    function setSelectedFilterParams(newQueries: {
      [key: string]: string | undefined
    }) {
      // Add static filters
      selectedFilterParams.value.brands = newQueries.brands?.split(',') || []
      selectedFilterParams.value.priceRange['min-price'] =
        newQueries['min-price'] || ''
      selectedFilterParams.value.priceRange['max-price'] =
        newQueries['max-price'] || ''
      selectedFilterParams.value.others = newQueries.others?.split(',') || []

      // Add dynamic filters .e.g TypeFilters
      Object.keys(selectedFilterParams.value.types).forEach(key => {
        selectedFilterParams.value.types[key] =
          newQueries[key]?.split(',') || []
      })
    }

    function clearSelectedFilterParam(key: string) {
      selectedFilterParams.value[key] = []
    }

    function clearTypeSelection(typeSlug: string = '') {
      selectedFilterParams.value.types[typeSlug] = []
    }

    function clearAllSelection() {
      selectedFilterParams.value.brands = []
      selectedFilterParams.value.priceRange = {
        'min-price': '',
        'max-price': '',
      }
      Object.keys(selectedFilterParams.value.types).forEach(key => {
        selectedFilterParams.value.types[key] = []
      })
      selectedFilterParams.value.others = []
    }

    function updateSelectedPriceRange(
      selectedValues: number[],
      limit: PriceRangeFilter
    ) {
      const minValue = selectedValues[0]
      const maxValue = selectedValues[1]
      const { minPrice, maxPrice } = limit
      const updatedPriceRange = {
        'min-price': minPrice.toFixed(),
        'max-price': maxPrice.toFixed(),
      }
      if (minValue > minPrice && minValue < maxPrice) {
        updatedPriceRange['min-price'] = Number.isInteger(minValue)
          ? minValue.toFixed()
          : minValue.toFixed(2)
      }
      if (maxValue > minPrice && maxValue < maxPrice) {
        updatedPriceRange['max-price'] = Number.isInteger(maxValue)
          ? maxValue.toFixed()
          : maxValue.toFixed(2)
      }
      selectedFilterParams.value.priceRange = updatedPriceRange
    }

    function resolvePriceRange(data: PriceRangeFilter): PriceRangeFilter {
      return {
        minPrice: data.minPrice / 100,
        maxPrice: data.maxPrice / 100,
      }
    }

    function initSelectedFiltersState(productFilter: ProductFilter) {
      // Only mutates dynamic types .e.g typefilters
      productFilter.typeFilters.forEach(typeFilter => {
        selectedFilterParams.value.types[typeFilter.slugUrl] = []
      })
    }

    /**
     * - - - - - Exposed - - - - -
     */
    return {
      setSelectedCategory,
      setToCloseAllMwebModals,
      setSelectedFilterParams,
      selectedFilterParams,
      selectedCategory,
      toCloseAllMwebModals,
      clearSelectedFilterParam,
      clearTypeSelection,
      clearAllSelection,
      updateSelectedPriceRange,
      resolvePriceRange,
      initSelectedFiltersState,
    }
  }
)
